// Per-page SEO component built on top of react-helmet-async.
//
// Responsibilities:
//   • Inject <title>, <meta description>, <meta keywords>, canonical URL.
//   • Emit Open Graph + Twitter Card tags for rich social previews.
//   • Emit one <link rel="alternate" hreflang="..."> per supported
//     language (plus x-default) so Google can serve the right locale.
//   • Inject optional JSON-LD structured data (WebApplication,
//     SoftwareApplication, BreadcrumbList, FAQPage, HowTo) for rich
//     results. Accepts a single object OR an array of objects so a page
//     can stack BreadcrumbList + SoftwareApplication + FAQPage.
//
// Backwards-compat note: the component originally only accepted `path`.
// Many tool pages were written passing `canonical` instead, so we now
// accept both — if `canonical` is provided it wins.
//
// Usage:
//   <Seo titleKey="seoHomeTitle" descriptionKey="seoHomeDesc" path="/" />
//   <Seo title="Custom" description="..." canonical="/basic-box-generator"
//        keywords={['laser cut','MakerCase alternative']} />

import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useI18n, LANGUAGES, LANGUAGE_ORDER } from '../i18n';
import { getSeoOverride, subscribeSeoOverrides } from './seoStore';
// Centralised public origin — single source of truth so the canonical,
// hreflang, og:url and og:image all stay in sync. See ../lib/siteUrl.js
// for the resolution order (env var > makercutt.com apex).
import { SITE_URL } from '../lib/siteUrl';

const DEFAULT_OG_IMAGE = `${SITE_URL}/og-cover.png`;

// ── Brand identity ────────────────────────────────────────────────────
// Canonical brand name is "MakerCutt" (two t's, no space). These are
// the documented spelling variations users commonly type:
//   • "Maker Cutt"  — with a space
//   • "MakerCut"    — single t (the original domain we owned: makercut.pages.dev)
//   • "Maker Cut"   — with a space AND single t
//   • "Makercutt"   — lowercase variant (Google normalises case, but
//                     declared anyway for completeness)
//
// We surface these as `alternateName` in every Organization /
// WebApplication / SoftwareApplication JSON-LD payload. Google reads
// `alternateName` as an official brand-synonym signal for the
// Knowledge Graph, so searches for "maker cut" or "maker cutt" will
// resolve to MakerCutt without us having to spam the typo into page
// titles or visible copy.
//
// NOTE: Order matters slightly — Google tends to weigh the FIRST few
// entries more. We lead with the most common typo ("Maker Cutt", from
// people who instinctively space the two halves) and end with the
// least likely one (lowercase one-word, only relevant for old
// pre-rebrand backlinks).
const BRAND_CANONICAL_NAME = 'MakerCutt';
const BRAND_ALT_NAMES = ['Maker Cutt', 'MakerCut', 'Maker Cut', 'Makercutt'];

export default function Seo({
  title,
  titleKey,
  description,
  descriptionKey,
  // Accept either `path` or `canonical` for the URL-relative segment.
  // Many older callers in /src/pages pass `canonical`, we keep both
  // working so we don't need a sweeping rename in this PR.
  path,
  canonical,
  image,
  type = 'website',
  jsonLd,
  // Array of strings — emitted as a comma-joined <meta name="keywords">
  // and (when paired with SoftwareApplication JSON-LD) inside the
  // structured-data keywords field. Google ignores meta keywords but
  // Bing, Yandex and Baidu still use them.
  keywords,
  // `locale` prop is accepted but ignored — we always derive locale from
  // i18n context. Older callers pass it; we don't want them to break.
  // eslint-disable-next-line no-unused-vars
  locale: _localeProp,
  noindex = false,
}) {
  const { t, lang, locale } = useI18n();

  // Re-render whenever the admin store updates (lets a Save in the
  // SEO editor reflect immediately in every open tab on the same
  // session, without requiring a full page reload).
  const [, setOverrideTick] = useState(0);
  useEffect(() => subscribeSeoOverrides(() => setOverrideTick((n) => n + 1)), []);

  // Resolve the canonical path: prefer explicit `canonical`, fall back
  // to `path`, finally to '/' so we always emit a valid URL.
  const resolvedPath = canonical || path || '/';

  // Resolution cascade: admin override (locale-specific or '*') > prop > i18n key > i18n fallback.
  const override = getSeoOverride(resolvedPath, lang) || {};

  const baseTitle = title || (titleKey ? t(titleKey) : t('seoSiteTitle'));
  const baseDescription = description || (descriptionKey ? t(descriptionKey) : t('seoSiteDescription'));

  const finalTitle = override.title || baseTitle;
  const finalDescription = override.description || baseDescription;
  const finalImage = override.og_image || image || DEFAULT_OG_IMAGE;
  const finalNoindex = noindex || !!override.noindex;
  const finalKeywords = Array.isArray(keywords) && keywords.length
    ? keywords.filter(Boolean).join(', ')
    : (override.keywords || '');
  const canonicalUrl = `${SITE_URL}${resolvedPath}`;
  const siteName = t('seoSiteName') || 'MakerCutt';

  // Accept either a single object or an array of objects; falsy entries
  // are filtered so callers can do `jsonLd={[a, b && c]}` without care.
  const jsonLdList = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : (jsonLd ? [jsonLd] : []);

  return (
    <Helmet>
      {/* Standard meta */}
      <html lang={locale} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <link rel="canonical" href={canonicalUrl} />
      {finalNoindex && <meta name="robots" content="noindex, nofollow" />}
      {!finalNoindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}

      {/* hreflang alternates — one per supported language + x-default */}
      {LANGUAGE_ORDER.map((code) => {
        const meta = LANGUAGES[code];
        if (!meta) return null;
        const url = code === 'pt'
          ? `${SITE_URL}${resolvedPath}`
          : `${SITE_URL}${resolvedPath}${resolvedPath.includes('?') ? '&' : '?'}lang=${code}`;
        return <link key={code} rel="alternate" hrefLang={meta.code} href={url} />;
      })}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${resolvedPath}`} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={locale.replace('-', '_')} />
      {LANGUAGE_ORDER.filter((c) => c !== lang).map((c) => (
        <meta key={c} property="og:locale:alternate" content={(LANGUAGES[c]?.code || c).replace('-', '_')} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      {/* JSON-LD structured data (one <script> per entry so we never
          merge incompatible @types into a single object). */}
      {jsonLdList.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

// Reusable JSON-LD builders -------------------------------------------------

/** WebApplication for the site landing — broad, organisation-level entry. */
export function buildWebAppJsonLd({ name = BRAND_CANONICAL_NAME, description, url = SITE_URL } = {}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name,
    // Brand-spelling typos surfaced as official synonyms — see
    // BRAND_ALT_NAMES at the top of this file.
    alternateName: BRAND_ALT_NAMES,
    description,
    url,
    applicationCategory: 'DesignApplication',
    operatingSystem: 'Any (web)',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
  };
}

/**
 * Organization payload — the strongest Knowledge-Graph signal for
 * brand identity. Emit ONCE from the Home page (don't duplicate on
 * every tool page; multiple Organization entries dilute the signal).
 *
 * Setting `alternateName` here teaches Google that "Maker Cutt",
 * "MakerCut", "Maker Cut" and "Makercutt" all refer to the same
 * entity, so typo searches resolve to us instead of bouncing through
 * a disambiguation page.
 */
export function buildOrganizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: BRAND_CANONICAL_NAME,
    alternateName: BRAND_ALT_NAMES,
    url: SITE_URL,
    logo: `${SITE_URL}/logo512.png`,
    sameAs: [
      'https://www.instagram.com/makercutt',
    ],
  };
}

/**
 * SoftwareApplication entry per tool. Optionally enriched with:
 *   • keywords  — string OR array of strings (we'll join them).
 *   • features  — array of strings ("Live preview", "DXF export", …).
 *   • image     — absolute or relative og:image URL.
 *   • aggregateRating — { ratingValue, ratingCount } if we have real
 *     review data from analytics later on.
 */
export function buildSoftwareJsonLd({
  name,
  description,
  url,
  slug,
  keywords,
  features,
  image,
  aggregateRating,
} = {}) {
  const fullUrl = url || `${SITE_URL}/box/${slug || ''}`;
  const kw = Array.isArray(keywords) ? keywords.join(', ') : (keywords || undefined);
  const data = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    // Tool-level brand synonyms — same list as the Organization and
    // WebApplication payloads. Helps disambiguate when a user searches
    // for e.g. "maker cut gear generator".
    alternateName: BRAND_ALT_NAMES,
    description,
    url: fullUrl.startsWith('http') ? fullUrl : `${SITE_URL}${fullUrl}`,
    applicationCategory: 'DesignApplication',
    applicationSubCategory: 'CADApplication',
    operatingSystem: 'Any (web)',
    browserRequirements: 'Requires JavaScript and a modern browser (Chrome, Firefox, Edge, Safari).',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      // Hint to Google that there's also a paid pay-per-download option,
      // so it doesn't tag the page as "free only" and remove the price
      // snippet when we later display the R$ 3,90 / $0,99 PPD modal.
      eligibleRegion: 'Worldwide',
    },
    publisher: {
      '@type': 'Organization',
      name: BRAND_CANONICAL_NAME,
      alternateName: BRAND_ALT_NAMES,
      url: SITE_URL,
    },
  };
  if (kw) data.keywords = kw;
  if (Array.isArray(features) && features.length) data.featureList = features.join(', ');
  if (image) data.image = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  if (aggregateRating && aggregateRating.ratingValue && aggregateRating.ratingCount) {
    data.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: String(aggregateRating.ratingValue),
      ratingCount: String(aggregateRating.ratingCount),
    };
  }
  return data;
}

/** Breadcrumb list for nested pages — Google renders this as the path. */
export function buildBreadcrumbJsonLd(items = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: it.url.startsWith('http') ? it.url : `${SITE_URL}${it.url}`,
    })),
  };
}

/**
 * FAQ JSON-LD for tool pages with a Q&A section. `items` is an array of
 * { question, answer } pairs. Google may render these as expanded FAQ
 * snippets directly in the SERP.
 */
export function buildFaqJsonLd(items = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.filter((i) => i && i.question && i.answer).map((i) => ({
      '@type': 'Question',
      name: i.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: i.answer,
      },
    })),
  };
}

/**
 * HowTo JSON-LD for step-by-step tutorials. `steps` is an array of
 * { name, text } objects in execution order.
 */
export function buildHowToJsonLd({ name, description, steps = [], totalTime, image } = {}) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((s, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: s.name,
      text: s.text,
    })),
  };
  if (totalTime) data.totalTime = totalTime;
  if (image) data.image = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  return data;
}

/**
 * ItemList JSON-LD for catalogue / index pages (Home, Examples).
 *
 * When Google sees this on the home page it understands the page is an
 * index of tools and is much more likely to surface inner links as
 * sitelinks (the cluster of secondary links under the main SERP entry)
 * instead of treating the home page as a single document.
 *
 * `items` is an array of { name, url, description? } in catalogue order.
 * We deliberately don't emit description by default — keep the payload
 * compact unless the caller really has copy worth shipping.
 *
 * Usage on Home:
 *   buildItemListJsonLd({
 *     name: 'MakerCutt Tools',
 *     items: tools.map((t) => ({
 *       name: t.name.en,
 *       url: `/box/${t.slug}`,
 *     })),
 *   })
 */
export function buildItemListJsonLd({ name, items = [] } = {}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    numberOfItems: items.length,
    itemListElement: items.map((it, idx) => {
      const url = (it.url || '').startsWith('http') ? it.url : `${SITE_URL}${it.url}`;
      const listItem = {
        '@type': 'ListItem',
        position: idx + 1,
        url,
        name: it.name,
      };
      if (it.description) listItem.description = it.description;
      return listItem;
    }),
  };
}

